import React from 'react';
import { Device } from '../../lib/types';
import type { DeviceSortKey, SortDir } from '../../lib/types';
import type { FosterHome } from '../../lib/fosterAdapters';
import DeviceRow from './DeviceRow';
import { SkeletonRows } from '../../components/SkeletonRow/SkeletonRow';
import Pagination from '../../components/Pagination/Pagination';
import Button from '../../components/Button/Button';
import TableHead, { type TableColumn } from './TableHead';
import CollapsibleSection from './CollapsibleSection';
import { ACTION_COLUMN_WIDTH } from './columnWidths';
import { SETUP_STATUS_TOOLTIP } from './setupStatus';
import styles from './Devices.module.scss';

/** Re-exported so callers of this table can name its sort keys without a second import. */
export type { DeviceSortKey };

interface DeviceSectionTableProps {
  title: string;
  description: string;
  /** Plural noun used in the empty-state copy, e.g. "activated devices". */
  emptyLabel: string;
  /** Show the Holder column. False hides it (e.g. Activated, where the holder is always the rescue). */
  showHolder?: boolean;
  /**
   * Show the "Placed On" column (the foster placement date). Only meaningful for the
   * Fosters section — an Activated row has no placement, so it stays off by default.
   */
  showPlacedOn?: boolean;
  devices: Device[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  skeletonCount: number;
  searchQuery: string;
  /**
   * Enables click-to-sort on the sortable headers. Provide all three to opt in;
   * omit them to render plain, unsortable headers. `sortKey` is null when no
   * column is active yet — the Fosters section opens on one, so it never is.
   */
  sortKey?: DeviceSortKey | null;
  sortDir?: SortDir;
  onSort?: (key: DeviceSortKey) => void;
  /**
   * This section's query failed. Shown as an error + Retry *in place of* the rows, so it only
   * takes over when there are none to lose — see the render below.
   */
  hasError?: boolean;
  onRetry?: () => void;
  onAddPet: (device: Device) => void;
  onTransferFoster: (device: Device) => void;
  onRecall: (device: Device) => void;
  onTransferAdopter: (device: Device) => void;
  /** One-click adopt-out using the foster household's info; only the Fosters section wires this. */
  onAdoptFromFoster?: (device: Device) => void;
  onEditScheduled?: (device: Device) => void;
  onCancelScheduled?: (device: Device) => void;
  /** Opens the saved foster home behind a placement; only the Fosters section wires this. */
  onViewFoster?: (home: FosterHome) => void;
  /** The rescue's saved homes, needed to resolve which one a placement belongs to. */
  fosterHomes?: FosterHome[];
  onSendSms: (device: Device) => void;
  onMarkLost?: (device: Device) => void;
  onMarkInoperable?: (device: Device) => void;
}

/**
 * The header row for a given combination of optional columns. Widths are spelled out per
 * layout rather than shared, because dropping or adding a column has to redistribute the
 * whole 100% — the surrounding cells absorb it, they don't just shift.
 *
 * There is no Battery column: {@link DeviceRow} stacks the reading at the foot of the
 * Device cell, on its own line, so it costs that column no extra width. Action holds a
 * single menu trigger rather than a row of buttons, so it is sized for one short label.
 *
 * Kept in step with the cells {@link DeviceRow} renders for the same two flags.
 */
export function buildDeviceColumns(showHolder: boolean, showPlacedOn: boolean): TableColumn<DeviceSortKey>[] {
  if (!showHolder) {
    // DeviceRow renders the Placed On cell on `showPlacedOn` alone, so this branch has to
    // honour it too — otherwise the pair (false, true) builds a five-column header over
    // six-cell rows, shifting every column right of Device and losing the shared action
    // offset. Covered by the width invariant test for all four flag combinations.
    return showPlacedOn
      ? [
          { label: 'Pet', width: '22%', sortKey: 'pet' },
          { label: 'Device', width: '22%', sortKey: 'device' },
          { label: 'Placed On', width: '16%', sortKey: 'placed' },
          { label: 'Setup', width: '13%', tooltip: SETUP_STATUS_TOOLTIP },
          { label: 'Status', width: '14%' },
          { label: 'Action', width: `${ACTION_COLUMN_WIDTH}%`, hideLabel: true },
        ]
      : [
          { label: 'Pet', width: '25%', sortKey: 'pet' },
          { label: 'Device', width: '25%', sortKey: 'device' },
          { label: 'Setup', width: '18%', tooltip: SETUP_STATUS_TOOLTIP },
          { label: 'Status', width: '19%' },
          { label: 'Action', width: `${ACTION_COLUMN_WIDTH}%`, hideLabel: true },
        ];
  }
  if (showPlacedOn) {
    return [
      { label: 'Holder', width: '16%' },
      { label: 'Pet', width: '16%', sortKey: 'pet' },
      { label: 'Device', width: '18%', sortKey: 'device' },
      { label: 'Placed On', width: '14%', sortKey: 'placed' },
      { label: 'Setup', width: '11%', tooltip: SETUP_STATUS_TOOLTIP },
      { label: 'Status', width: '12%' },
      { label: 'Action', width: `${ACTION_COLUMN_WIDTH}%`, hideLabel: true },
    ];
  }
  // No caller uses this combination today (Activated hides Holder, Fosters shows Placed On),
  // but it is a valid one, so it holds the same invariant rather than drifting unnoticed.
  return [
    { label: 'Holder', width: '19%' },
    { label: 'Pet', width: '19%', sortKey: 'pet' },
    { label: 'Device', width: '21%', sortKey: 'device' },
    { label: 'Setup', width: '14%', tooltip: SETUP_STATUS_TOOLTIP },
    { label: 'Status', width: '14%' },
    { label: 'Action', width: `${ACTION_COLUMN_WIDTH}%`, hideLabel: true },
  ];
}

/**
 * A section card listing devices attached to pets (via {@link DeviceRow}).
 * Shared by the "Activated" and "Devices with a Foster" panels — they differ
 * only in copy, which list feeds them, and which optional columns they show.
 */
export default function DeviceSectionTable({
  title,
  description,
  emptyLabel,
  showHolder = true,
  showPlacedOn = false,
  devices,
  totalCount,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  skeletonCount,
  searchQuery,
  sortKey,
  sortDir,
  onSort,
  hasError,
  onRetry,
  onAddPet,
  onTransferFoster,
  onRecall,
  onTransferAdopter,
  onAdoptFromFoster,
  onEditScheduled,
  onCancelScheduled,
  onViewFoster,
  fosterHomes,
  onSendSms,
  onMarkLost,
  onMarkInoperable,
}: DeviceSectionTableProps) {
  const columns = buildDeviceColumns(showHolder, showPlacedOn);
  const colCount = columns.length;
  const rowProps = {
    onAddPet,
    onTransferFoster,
    onRecall,
    onTransferAdopter,
    onAdoptFromFoster,
    onEditScheduled,
    onCancelScheduled,
    onViewFoster,
    fosterHomes,
    onSendSms,
    onMarkLost,
    onMarkInoperable,
    showHolder,
    showPlacedOn,
  };

  // Use the dimming overlay when we have stale data to keep visible (prevents a
  // height jump); only fall back to skeleton rows when there's no data at all.
  const hasData = devices.length > 0;
  const showOverlay = skeletonCount > 0 && hasData;
  const showSkeleton = skeletonCount > 0 && !hasData;
  const wrapperClass = `${styles.tableWrapper}${showOverlay ? ` ${styles.tableTransitioning}` : ''}`;

  return (
    <CollapsibleSection title={title} count={totalCount} description={description}>
      <div className={wrapperClass}>
        <table className={styles.table}>
          <TableHead columns={columns} sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
          <tbody>
            {/*
              Only when there is nothing else to show. Under `cache-and-network` a failed
              refetch raises `error` while `previousData` still holds a full page, and every
              mutation on the page refetches active queries — so blanking on `hasError` alone
              would drop the user out of a table they were working in over one bad response.
            */}
            {hasError && !hasData ? (
              <tr>
                <td colSpan={colCount} className={styles.errorRow} role="alert">
                  <span className={styles.errorRowContent}>
                    <span>Could not load {emptyLabel}.</span>
                    {onRetry && (
                      <Button variant="ghost" size="sm" onClick={onRetry}>
                        Retry
                      </Button>
                    )}
                  </span>
                </td>
              </tr>
            ) : showSkeleton ? (
              <SkeletonRows count={skeletonCount} columns={colCount} />
            ) : devices.length === 0 ? (
              <tr>
                <td colSpan={colCount} className={styles.emptyState}>
                  {searchQuery ? `No ${emptyLabel} matching "${searchQuery}"` : `No ${emptyLabel}`}
                </td>
              </tr>
            ) : (
              devices.map((d) => <DeviceRow key={d.id} device={d} {...rowProps} />)
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </CollapsibleSection>
  );
}
